import React from 'react';
import { Image, Text, View, StyleSheet } from '@react-pdf/renderer';
import {
  Section,
  pdfColors, pdfFontFamily, pdfFontSize, pdfRadius, pdfSpacing,
} from '../../../utils/pdf';
import type {
  AdmissionCycle, AdmissionField, AdmissionFieldOption, AdmissionFormSection, IdNamePair,
} from '../../../types/admissions';
import { activeFormSections, isCnicFieldKey, withoutPhoto } from '../form/admissionFormFields';

/**
 * Input affordances for printed admission forms. Turns an `AdmissionField`
 * into a writing space matched to its type — a ruled line for text, a
 * segmented comb for CNIC and dates, tick boxes for choices — without a
 * bespoke template per cycle.
 *
 * Every affordance occupies the same INPUT_HEIGHT, so two fields sharing a
 * row always line up, whatever their types. Shared between the blank form
 * (empty) and any filled rendering (value baked in).
 */

const INPUT_HEIGHT = 22;
const CELL_WIDTH = 14;

const styles = StyleSheet.create({
  fieldWrap: {
    marginBottom: 10,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 2,
  },
  label: {
    fontSize: pdfFontSize.xs,
    color: pdfColors.textSecondary,
    fontWeight: 500,
  },
  required: {
    color: pdfColors.danger,
    fontWeight: 700,
  },

  // ── Ruled line ──
  line: {
    height: INPUT_HEIGHT,
    justifyContent: 'flex-end',
    borderBottomWidth: 0.7,
    borderBottomColor: pdfColors.dividerStrong,
    borderBottomStyle: 'solid',
    paddingBottom: 2,
    paddingLeft: 2,
  },
  lineValue: {
    fontSize: pdfFontSize.md,
    color: pdfColors.textPrimary,
    fontWeight: 500,
  },
  ghost: {
    fontSize: pdfFontSize.xxs,
    color: pdfColors.textFaint,
  },

  // ── Ruled multi-line block ──
  block: {
    borderWidth: 0.7,
    borderColor: pdfColors.dividerStrong,
    borderStyle: 'solid',
    borderRadius: 3,
    paddingHorizontal: 6,
  },
  blockLine: {
    height: 18,
    borderBottomWidth: 0.4,
    borderBottomColor: pdfColors.divider,
    borderBottomStyle: 'dotted',
  },
  blockValue: {
    paddingVertical: 6,
    fontSize: pdfFontSize.md,
    color: pdfColors.textPrimary,
    lineHeight: 1.45,
  },

  // ── Comb (CNIC / date) ──
  combRow: {
    height: INPUT_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
  },
  combGroup: {
    flexDirection: 'row',
    height: 20,
    borderWidth: 0.7,
    borderColor: pdfColors.dividerStrong,
    borderStyle: 'solid',
    borderRadius: 2,
  },
  combCell: {
    width: CELL_WIDTH,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  combDivider: {
    borderLeftWidth: 0.5,
    borderLeftColor: pdfColors.divider,
    borderLeftStyle: 'solid',
  },
  combDigit: {
    fontFamily: pdfFontFamily.mono,
    fontSize: pdfFontSize.md,
    color: pdfColors.textPrimary,
  },
  combGhost: {
    fontSize: pdfFontSize.xxs,
    color: pdfColors.dividerStrong,
    fontWeight: 500,
  },
  combSep: {
    width: 10,
    textAlign: 'center',
    fontSize: pdfFontSize.md,
    color: pdfColors.textMuted,
  },

  // ── Tick boxes ──
  choiceRow: {
    minHeight: INPUT_HEIGHT,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  choice: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: pdfSpacing.lg,
    paddingVertical: 2,
  },
  box: {
    width: 10,
    height: 10,
    borderWidth: 0.8,
    borderColor: pdfColors.textSecondary,
    borderStyle: 'solid',
    borderRadius: 1.5,
    marginRight: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxTick: {
    fontSize: 8,
    lineHeight: 1,
    color: pdfColors.brand,
    fontWeight: 700,
  },
  choiceLabel: {
    fontSize: pdfFontSize.sm,
    color: pdfColors.textPrimary,
  },

  // ── Section layout ──
  row: {
    flexDirection: 'row',
    marginHorizontal: -pdfSpacing.md,
  },
  col: {
    flex: 1,
    paddingHorizontal: pdfSpacing.md,
  },
  lead: {
    flexDirection: 'row',
  },
  leadFields: {
    flex: 1,
    marginRight: pdfSpacing.lg,
  },
  photo: {
    marginBottom: pdfSpacing.md,
    borderWidth: 0.8,
    borderStyle: 'dashed',
    borderColor: pdfColors.dividerStrong,
    borderRadius: pdfRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  photoImage: {
    marginBottom: pdfSpacing.md,
    borderRadius: pdfRadius.sm,
    objectFit: 'cover',
  },
  photoTitle: {
    fontSize: pdfFontSize.xs,
    fontWeight: 600,
    color: pdfColors.textSecondary,
    textAlign: 'center',
  },
  photoHint: {
    fontSize: pdfFontSize.xxs,
    color: pdfColors.textFaint,
    textAlign: 'center',
    marginTop: 3,
  },
});

export interface FieldRenderOptions {
  /** When provided, the field renders in "filled" mode: the user's answer
   *  is baked in, boxes get a tick, CNIC digits fill the comb. When omitted,
   *  the field renders in "blank" mode (printable form). */
  value?: unknown;
  /** Choice list to print instead of `field.options` — e.g. the cycle's
   *  allowed classes for the class picker, whose options live elsewhere. */
  choices?: AdmissionFieldOption[];
}

/** Choice lists longer than this print as a ruled line instead of boxes. */
const MAX_PRINTED_CHOICES = 8;

// `helpText` is deliberately not printed: it is written for staff using the
// portal ("…otherwise the Family stays in candidate state"), not for parents.
export const FieldLabel: React.FC<{ field: AdmissionField }> = ({ field }) => (
  <View style={styles.labelRow}>
    <Text style={styles.label}>
      {field.label}
      {field.required && <Text style={styles.required}> *</Text>}
    </Text>
  </View>
);

/** System CNIC fields, plus custom fields whose label says CNIC / B-Form. */
export function isCnicField(field: AdmissionField): boolean {
  return isCnicFieldKey(field.key) || /cnic|b-?form/i.test(field.label);
}

/** Renders ONE field — label + input affordance — matched to its type. */
export const FieldBlock: React.FC<{ field: AdmissionField; options?: FieldRenderOptions }> = ({ field, options }) => {
  const value = options?.value;
  const hasValue = value != null && value !== '';
  const text = hasValue ? (Array.isArray(value) ? value.join(', ') : String(value)) : '';

  let input: React.ReactNode;
  switch (field.type) {
    case 'textarea':
      input = (
        <View style={styles.block}>
          {hasValue ? (
            <Text style={styles.blockValue}>{text}</Text>
          ) : (
            [0, 1, 2].map((i) => <View key={i} style={styles.blockLine} />)
          )}
        </View>
      );
      break;
    case 'boolean':
      input = (
        <Choices
          choices={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
          selected={hasValue ? [String(value).toLowerCase() === 'true' ? 'yes' : String(value).toLowerCase()] : []}
        />
      );
      break;
    case 'dropdown':
    case 'multiselect': {
      const choices = options?.choices ?? field.options ?? [];
      const selected = Array.isArray(value) ? value.map(String) : hasValue ? [String(value)] : [];
      input = choices.length === 0 || choices.length > MAX_PRINTED_CHOICES
        ? <RuledLine value={text} />
        : <Choices choices={choices} selected={selected} />;
      break;
    }
    case 'date':
      input = <DateComb value={text} />;
      break;
    default:
      input = isCnicField(field) ? <CnicComb value={text} /> : <RuledLine value={text} />;
      break;
  }

  return (
    <View style={styles.fieldWrap} wrap={false}>
      <FieldLabel field={field} />
      {input}
    </View>
  );
};

const RuledLine: React.FC<{ value: string; ghost?: string }> = ({ value, ghost }) => (
  <View style={styles.line}>
    {value ? (
      <Text style={styles.lineValue}>
        {value}
      </Text>
    ) : ghost ? (
      <Text style={styles.ghost}>{ghost}</Text>
    ) : null}
  </View>
);

const Choices: React.FC<{ choices: AdmissionFieldOption[]; selected: string[] }> = ({ choices, selected }) => (
  <View style={styles.choiceRow}>
    {choices.map((o) => (
      <View key={o.value} style={styles.choice}>
        <View style={styles.box}>
          {selected.includes(o.value) && <Text style={styles.boxTick}>✓</Text>}
        </View>
        <Text style={styles.choiceLabel}>{o.label}</Text>
      </View>
    ))}
  </View>
);

/** One bordered comb segment of `cells` boxes. `chars` fills them; `ghosts`
 *  prints a faint placeholder letter in each empty box (D D / M M …). */
const CombGroup: React.FC<{ chars: string[]; ghosts?: string[] }> = ({ chars, ghosts }) => (
  <View style={styles.combGroup}>
    {chars.map((c, i) => (
      <View key={i} style={i === 0 ? styles.combCell : [styles.combCell, styles.combDivider]}>
        {c ? (
          <Text style={styles.combDigit}>{c}</Text>
        ) : ghosts ? (
          <Text style={styles.combGhost}>{ghosts[i]}</Text>
        ) : null}
      </View>
    ))}
  </View>
);

/** Splits `value`'s digits across segments of the given sizes. */
function segment(value: string, sizes: number[]): string[][] {
  const total = sizes.reduce((a, b) => a + b, 0);
  const digits = (value || '').replace(/\D/g, '').slice(0, total).split('');
  const padded = [...digits, ...Array(total - digits.length).fill('')];
  const out: string[][] = [];
  let at = 0;
  for (const n of sizes) { out.push(padded.slice(at, at + n)); at += n; }
  return out;
}

/** 13-box CNIC / B-Form comb in NADRA's 5–7–1 segmentation. */
export const CnicComb: React.FC<{ value: string }> = ({ value }) => {
  const [a, b, c] = segment(value, [5, 7, 1]);
  return (
    <View style={styles.combRow}>
      <CombGroup chars={a} />
      <Text style={styles.combSep}>–</Text>
      <CombGroup chars={b} />
      <Text style={styles.combSep}>–</Text>
      <CombGroup chars={c} />
    </View>
  );
};

/** DD / MM / YYYY comb with faint placeholder letters when blank. A filled
 *  ISO date (YYYY-MM-DD…) is re-ordered into the day-first boxes. */
const DateComb: React.FC<{ value: string }> = ({ value }) => {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  const dayFirst = iso ? `${iso[3]}${iso[2]}${iso[1]}` : '';
  const [d, m, y] = segment(dayFirst, [2, 2, 4]);
  return (
    <View style={styles.combRow}>
      <CombGroup chars={d} ghosts={['D', 'D']} />
      <Text style={styles.combSep}>/</Text>
      <CombGroup chars={m} ghosts={['M', 'M']} />
      <Text style={styles.combSep}>/</Text>
      <CombGroup chars={y} ghosts={['Y', 'Y', 'Y', 'Y']} />
    </View>
  );
};

// ─── Whole-form rendering ─────────────────────────────────────────────────

/** The cycle's form exactly as parents see it online — the photo prints in
 *  its own slot rather than as a field. */
export const printableSections = (cycle: AdmissionCycle): AdmissionFormSection[] =>
  withoutPhoto(activeFormSections(cycle.formTemplate?.sections));

/** Pairs fields two-up; textareas claim a full row. */
function toRows(fields: AdmissionField[]): AdmissionField[][] {
  const rows: AdmissionField[][] = [];
  let bucket: AdmissionField[] = [];
  for (const f of fields) {
    if (f.type === 'textarea') {
      if (bucket.length) { rows.push(bucket); bucket = []; }
      rows.push([f]);
    } else {
      bucket.push(f);
      if (bucket.length === 2) { rows.push(bucket); bucket = []; }
    }
  }
  if (bucket.length) rows.push(bucket);
  return rows;
}

/** Choice lists sourced from the cycle rather than the template. */
function choicesFor(field: AdmissionField, cycle: AdmissionCycle): AdmissionFieldOption[] | undefined {
  if (field.key !== 'classId') return undefined;
  return (cycle.allowedClassIds ?? [])
    .filter((c): c is IdNamePair => typeof c === 'object' && !!c?.name)
    .map((c) => ({ value: c._id, label: c.name }));
}

/** Fields stacked one-per-line beside the photograph in the first section. */
const FIELDS_BESIDE_PHOTO = 3;

/** The applicant's photo, or a dashed "affix photograph" box when there is
 *  none. Default size suits the A4 form; the admit card passes a smaller one.
 *  `src` is a data URL (see usePhotoDataUrl) so rendering never hits the network. */
export const PhotoSlot: React.FC<{ width?: number; height?: number; src?: string | null }> = ({ width = 96, height = 120, src }) => (
  src ? (
    <Image src={src} style={[styles.photoImage, { width, height }]} />
  ) : (
    <View style={[styles.photo, { width, height }]}>
      <Text style={styles.photoTitle}>Affix photograph</Text>
      <Text style={styles.photoHint}>Recent, passport size{'\n'}35 × 45 mm</Text>
    </View>
  )
);

interface FormSectionsProps {
  cycle: AdmissionCycle;
  /** Filled mode: the answer for a field key. Omit for a blank form. */
  answerFor?: (key: string) => unknown;
  /** Number of the first section heading (01 by default). */
  startIndex?: number;
  /** Filled mode: the applicant's photo as a data URL. */
  photoSrc?: string | null;
}

/**
 * Every section and field of the cycle's form, numbered, with the
 * photograph slot beside the opening fields of the first section. The blank
 * form and the filled application both render through this, so the two
 * printouts always mirror each other field for field.
 */
export const FormSections: React.FC<FormSectionsProps> = ({ cycle, answerFor, startIndex = 1, photoSrc }) => {
  const field = (f: AdmissionField) => (
    <FieldBlock
      field={f}
      options={{ value: answerFor?.(f.key), choices: choicesFor(f, cycle) }}
    />
  );

  return (
    <>
      {printableSections(cycle).map((section, si) => {
        let rest = section.fields;
        let lead: AdmissionField[] = [];
        if (si === 0) {
          const n = rest.findIndex((f) => f.type === 'textarea');
          const take = Math.min(FIELDS_BESIDE_PHOTO, n === -1 ? rest.length : n);
          lead = rest.slice(0, take);
          rest = rest.slice(take);
        }
        return (
          <Section key={section.key} index={startIndex + si} title={section.title}>
            {si === 0 && (
              <View style={styles.lead} wrap={false}>
                <View style={styles.leadFields}>
                  {lead.map((f) => <React.Fragment key={f.key}>{field(f)}</React.Fragment>)}
                </View>
                <PhotoSlot src={photoSrc} />
              </View>
            )}
            {toRows(rest).map((row, i) => (
              <View key={i} style={styles.row} wrap={false}>
                {row.map((f) => <View key={f.key} style={styles.col}>{field(f)}</View>)}
                {row.length === 1 && row[0].type !== 'textarea' && <View style={styles.col} />}
              </View>
            ))}
          </Section>
        );
      })}
    </>
  );
};
